/**
 * Strategy Builder Types
 *
 * Complete type definitions for the visual strategy builder.
 * Maps 1:1 to TradeCraft DSL AST nodes.
 *
 * DSL Reference: docs/TradeCraft_DSL.md
 */

// ─── Signal Types ─────────────────────────────────────────────

export type SignalType =
  | 'rsi'
  | 'cumulative-return'
  | 'current-price'
  | 'moving-average-price'
  | 'exponential-moving-average-price'
  | 'moving-average-return'
  | 'max-drawdown'
  | 'stdev-return'
  | 'stdev-price';

export interface Signal {
  type: SignalType;
  ticker: string;
  window?: number; // undefined for current-price
}

/** Signal without ticker — used in filter nodes where signal applies to each child */
export type FilterSignal = Omit<Signal, 'ticker'>;

export const SIGNAL_LABELS: Record<SignalType, string> = {
  'rsi': 'Momentum Index',
  'cumulative-return': 'Price Change',
  'current-price': 'Spot Price',
  'moving-average-price': 'Average Price',
  'exponential-moving-average-price': 'Weighted Avg Price',
  'moving-average-return': 'Avg Return',
  'max-drawdown': 'Max Decline',
  'stdev-return': 'Return Volatility',
  'stdev-price': 'Price Volatility',
};

/** TC-DSL signal method names: TICKER.method(window) */
export const SIGNAL_DSL_NAMES: Record<SignalType, string> = {
  'rsi': 'rsi',
  'cumulative-return': 'return',
  'current-price': 'price',
  'moving-average-price': 'sma',
  'exponential-moving-average-price': 'ema',
  'moving-average-return': 'ma_return',
  'max-drawdown': 'maxdd',
  'stdev-return': 'stdev',
  'stdev-price': 'stdev_price',
};

export const SIGNAL_HAS_WINDOW: Record<SignalType, boolean> = {
  'rsi': true,
  'cumulative-return': true,
  'current-price': false,
  'moving-average-price': true,
  'exponential-moving-average-price': true,
  'moving-average-return': true,
  'max-drawdown': true,
  'stdev-return': true,
  'stdev-price': true,
};

export const SIGNAL_DEFAULT_WINDOW: Record<SignalType, number> = {
  'rsi': 14,
  'cumulative-return': 5,
  'current-price': 0,
  'moving-average-price': 200,
  'exponential-moving-average-price': 20,
  'moving-average-return': 30,
  'max-drawdown': 20,
  'stdev-return': 20,
  'stdev-price': 20,
};

// ─── Selector Types (for filters) ────────────────────────────

export type SelectorDirection = 'top' | 'bottom';

export interface Selector {
  direction: SelectorDirection;
  count: number;
}

// ─── Comparison Operators ────────────────────────────────────

export type CompareOperator = '>' | '<' | '>=' | '<=';

// ─── Condition ───────────────────────────────────────────────

export interface Condition {
  operator: CompareOperator;
  left: Signal;
  right: Signal | number;
}

// ─── Node Types ──────────────────────────────────────────────

export type NodeType =
  | 'asset'
  | 'group'
  | 'weight-equal'
  | 'weight-specified'
  | 'weight-inverse-volatility'
  | 'if'
  | 'filter';

interface BaseNode {
  id: string;
  type: NodeType;
  collapsed?: boolean;
}

export interface AssetNode extends BaseNode {
  type: 'asset';
  ticker: string;
  name?: string;
}

export interface GroupNode extends BaseNode {
  type: 'group';
  name: string;
  child: StrategyNode;
}

export interface WeightEqualNode extends BaseNode {
  type: 'weight-equal';
  children: StrategyNode[];
}

export interface WeightSpecifiedNode extends BaseNode {
  type: 'weight-specified';
  weights: number[]; // Parallel to children, each 0-1. Should sum to 1.0
  children: StrategyNode[];
}

export interface WeightInverseVolatilityNode extends BaseNode {
  type: 'weight-inverse-volatility';
  window: number;
  children: StrategyNode[];
}

export interface IfNode extends BaseNode {
  type: 'if';
  condition: Condition;
  thenBranch: StrategyNode;
  elseBranch: StrategyNode;
}

export interface FilterNode extends BaseNode {
  type: 'filter';
  signal: FilterSignal;
  selector: Selector;
  children: StrategyNode[];
}

export type StrategyNode =
  | AssetNode
  | GroupNode
  | WeightEqualNode
  | WeightSpecifiedNode
  | WeightInverseVolatilityNode
  | IfNode
  | FilterNode;

// ─── Strategy Metadata ───────────────────────────────────────

export type RebalanceMode = 'daily' | 'threshold';

export interface StrategyMetadata {
  name: string;
  description?: string;
  rebalanceMode: RebalanceMode;
  rebalanceThreshold?: number; // percentage, e.g. 5 = 5%
}

export interface Strategy {
  metadata: StrategyMetadata;
  root: StrategyNode;
}

// ─── Clipboard ───────────────────────────────────────────────

export interface ClipboardState {
  node: StrategyNode | null;
  operation: 'copy' | 'cut' | null;
  sourceId: string | null;
}

// ─── UI Metadata ─────────────────────────────────────────────

/** Which node types can be added as children of each parent type */
export const ALLOWED_CHILDREN: Record<NodeType, NodeType[]> = {
  'asset': [],
  'group': ['asset', 'group', 'weight-equal', 'weight-specified', 'weight-inverse-volatility', 'if', 'filter'],
  'weight-equal': ['asset', 'group', 'weight-equal', 'weight-specified', 'weight-inverse-volatility', 'if', 'filter'],
  'weight-specified': ['asset', 'group', 'weight-equal', 'weight-specified', 'weight-inverse-volatility', 'if', 'filter'],
  'weight-inverse-volatility': ['asset', 'group', 'weight-equal', 'weight-specified', 'weight-inverse-volatility', 'if', 'filter'],
  'if': [],
  'filter': ['asset'],
};

export const NODE_LABELS: Record<NodeType, string> = {
  'asset': 'Ticker',
  'group': 'Basket',
  'weight-equal': 'Equal Allocation',
  'weight-specified': 'Custom Allocation',
  'weight-inverse-volatility': 'Volatility Weighted',
  'if': 'Conditional',
  'filter': 'Selection',
};

export const NODE_DESCRIPTIONS: Record<NodeType, string> = {
  'asset': 'NYSE stock or ETF ticker',
  'group': 'Named container for organization',
  'weight-equal': 'Distribute equally among children',
  'weight-specified': 'Set custom percentage weights',
  'weight-inverse-volatility': 'Allocate by inverse volatility',
  'if': 'Conditional branching logic',
  'filter': 'Select top/bottom N by signal',
};

/** Node block color classes (theme-aware via CSS custom properties) */
export const NODE_COLORS: Record<NodeType, { bg: string; border: string; text: string; pill: string }> = {
  'asset': {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/20 text-emerald-300',
  },
  'group': {
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    pill: 'bg-purple-500/20 text-purple-300',
  },
  'weight-equal': {
    bg: 'bg-surface-700/50',
    border: 'border-surface-600',
    text: 'text-surface-300',
    pill: 'bg-surface-600/50 text-surface-200',
  },
  'weight-specified': {
    bg: 'bg-surface-700/50',
    border: 'border-surface-600',
    text: 'text-surface-300',
    pill: 'bg-surface-600/50 text-surface-200',
  },
  'weight-inverse-volatility': {
    bg: 'bg-surface-700/50',
    border: 'border-surface-600',
    text: 'text-surface-300',
    pill: 'bg-surface-600/50 text-surface-200',
  },
  'if': {
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
    text: 'text-sky-400',
    pill: 'bg-sky-500/20 text-sky-300',
  },
  'filter': {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    pill: 'bg-amber-500/20 text-amber-300',
  },
};

/** Returns true if this node type can contain children */
export function canHaveChildren(type: NodeType): boolean {
  return ALLOWED_CHILDREN[type].length > 0 || type === 'if' || type === 'group';
}

// ─── Factory Functions ───────────────────────────────────────

let _idCounter = 0;
export function generateNodeId(): string {
  _idCounter++;
  return `n_${Date.now().toString(36)}_${_idCounter.toString(36)}`;
}

export function createAssetNode(ticker = 'SPY', name?: string): AssetNode {
  return { id: generateNodeId(), type: 'asset', ticker, name };
}

export function createGroupNode(name = 'New Basket'): GroupNode {
  return { id: generateNodeId(), type: 'group', name, child: createAssetNode() };
}

export function createWeightEqualNode(children?: StrategyNode[]): WeightEqualNode {
  return {
    id: generateNodeId(),
    type: 'weight-equal',
    children: children || [createAssetNode('SPY'), createAssetNode('QQQ')],
  };
}

export function createWeightSpecifiedNode(): WeightSpecifiedNode {
  return {
    id: generateNodeId(),
    type: 'weight-specified',
    weights: [0.6, 0.4],
    children: [createAssetNode('SPY'), createAssetNode('BND')],
  };
}

export function createWeightInverseVolatilityNode(): WeightInverseVolatilityNode {
  return {
    id: generateNodeId(),
    type: 'weight-inverse-volatility',
    window: 20,
    children: [createAssetNode('SPY'), createAssetNode('TLT')],
  };
}

export function createIfNode(): IfNode {
  return {
    id: generateNodeId(),
    type: 'if',
    condition: {
      operator: '>',
      left: { type: 'rsi', ticker: 'SPY', window: 14 },
      right: 70,
    },
    thenBranch: createAssetNode('BIL'),
    elseBranch: createAssetNode('SPY'),
  };
}

export function createFilterNode(): FilterNode {
  return {
    id: generateNodeId(),
    type: 'filter',
    signal: { type: 'moving-average-return', window: 30 },
    selector: { direction: 'top', count: 1 },
    children: [createAssetNode('SPY'), createAssetNode('QQQ'), createAssetNode('IWM')],
  };
}

export function createNodeByType(type: NodeType): StrategyNode {
  switch (type) {
    case 'asset': return createAssetNode();
    case 'group': return createGroupNode();
    case 'weight-equal': return createWeightEqualNode();
    case 'weight-specified': return createWeightSpecifiedNode();
    case 'weight-inverse-volatility': return createWeightInverseVolatilityNode();
    case 'if': return createIfNode();
    case 'filter': return createFilterNode();
  }
}

export function createEmptyStrategy(): Strategy {
  return {
    metadata: { name: 'New Strategy', rebalanceMode: 'daily' },
    root: createWeightEqualNode(),
  };
}

// ─── Tree Helpers ────────────────────────────────────────────

/** Deep clone a node, regenerating all IDs */
export function cloneNode(node: StrategyNode): StrategyNode {
  const cloned: StrategyNode = JSON.parse(JSON.stringify(node));
  const regenIds = (n: StrategyNode) => {
    n.id = generateNodeId();
    if ('children' in n && Array.isArray(n.children)) n.children.forEach(regenIds);
    if ('child' in n && n.child) regenIds(n.child);
    if ('thenBranch' in n) { regenIds(n.thenBranch); regenIds(n.elseBranch); }
  };
  regenIds(cloned);
  return cloned;
}

/** Find a node by ID */
export function findNode(root: StrategyNode, id: string): StrategyNode | null {
  if (root.id === id) return root;
  if ('children' in root && Array.isArray(root.children)) {
    for (const c of root.children) { const f = findNode(c, id); if (f) return f; }
  }
  if ('child' in root && root.child) { const f = findNode(root.child, id); if (f) return f; }
  if ('thenBranch' in root) {
    const ft = findNode(root.thenBranch, id); if (ft) return ft;
    const fe = findNode(root.elseBranch, id); if (fe) return fe;
  }
  return null;
}

/** Find parent of a node. Returns {parent, key, index} */
export function findParent(
  root: StrategyNode,
  childId: string,
): { parent: StrategyNode; key: string; index: number } | null {
  if ('children' in root && Array.isArray(root.children)) {
    for (let i = 0; i < root.children.length; i++) {
      if (root.children[i].id === childId) return { parent: root, key: 'children', index: i };
      const f = findParent(root.children[i], childId);
      if (f) return f;
    }
  }
  if ('child' in root && root.child) {
    if (root.child.id === childId) return { parent: root, key: 'child', index: 0 };
    const f = findParent(root.child, childId);
    if (f) return f;
  }
  if ('thenBranch' in root) {
    if (root.thenBranch.id === childId) return { parent: root, key: 'thenBranch', index: 0 };
    if (root.elseBranch.id === childId) return { parent: root, key: 'elseBranch', index: 1 };
    const ft = findParent(root.thenBranch, childId); if (ft) return ft;
    const fe = findParent(root.elseBranch, childId); if (fe) return fe;
  }
  return null;
}

/** Count all nodes in tree */
export function countNodes(node: StrategyNode): number {
  let c = 1;
  if ('children' in node && Array.isArray(node.children)) for (const ch of node.children) c += countNodes(ch);
  if ('child' in node && node.child) c += countNodes(node.child);
  if ('thenBranch' in node) { c += countNodes(node.thenBranch); c += countNodes(node.elseBranch); }
  return c;
}

/** Get all tickers used in strategy */
export function getAllTickers(node: StrategyNode): string[] {
  const tickers = new Set<string>();
  const collect = (n: StrategyNode) => {
    if (n.type === 'asset' && n.ticker) tickers.add(n.ticker);
    if ('condition' in n) {
      if (n.condition.left.ticker) tickers.add(n.condition.left.ticker);
      if (typeof n.condition.right !== 'number' && n.condition.right.ticker) tickers.add(n.condition.right.ticker);
    }
    if ('children' in n && Array.isArray(n.children)) n.children.forEach(collect);
    if ('child' in n && n.child) collect(n.child);
    if ('thenBranch' in n) { collect(n.thenBranch); collect(n.elseBranch); }
  };
  collect(node);
  return Array.from(tickers);
}

/** Format a signal for inline display: "QQQ.rsi(10)" */
export function formatSignalInline(signal: Signal): string {
  const dsl = SIGNAL_DSL_NAMES[signal.type];
  if (signal.type === 'current-price') return `${signal.ticker}.${dsl}`;
  return `${signal.ticker}.${dsl}(${signal.window ?? ''})`;
}

/** Format a filter signal (no ticker): "ma_return(30)" */
export function formatFilterSignalInline(signal: FilterSignal): string {
  const dsl = SIGNAL_DSL_NAMES[signal.type];
  if (signal.type === 'current-price') return dsl;
  return `${dsl}(${signal.window ?? ''})`;
}
